import { z } from "zod";
import type {
  EquipSlot,
  ItemInstance,
  ItemTemplate,
} from "./data/items.js";
import { EQUIP_SLOTS, ItemTemplateSchema } from "./data/items.js";
import { type MobTemplate, MobTemplateSchema } from "./data/mobs.js";
import { type AffixDefinition, AffixPoolSchema } from "./data/affixes.js";
import { type QuestTemplate, QuestTemplateSchema } from "./data/quests.js";
import { type NpcTemplate, NpcTemplateSchema } from "./data/npcs.js";
import { type SpellTemplate, SpellTemplateSchema } from "./data/spells.js";
import { type GameConfig, GameConfigSchema } from "./data/game-config.js";
import { type ZoneJson, ZoneCatalogueSchema } from "./data/zones.js";
import type { ProjectileKind } from "./data/projectiles.js";
import type { BuffSnapshot, HotbarSlots } from "./data/spells.js";
import { HOTBAR_SIZE } from "./data/spells.js";
import { STAT_KEYS, type StatKey } from "./progression.js";
import type {
  Character,
  CharacterStats,
  ChatMessage,
  EquippedSlots,
  Facing,
  MobDebugSnapshot,
  MobSnapshot,
  NpcSnapshot,
  PlayerSnapshot,
  ProjectileSnapshot,
} from "./types.js";
import type { Portal, SpawnPoint, TerrainId } from "./world/tilemap.js";

export const PROTOCOL_VERSION = 1;

// M5.1 — vendor error taxonomy. Client humanizes via humanizeVendorError().
export type VendorErrorCode =
  | "vendor_not_found"
  | "out_of_range"
  | "not_enough_copper"
  | "too_heavy"
  | "item_not_stocked"
  | "instance_not_owned"
  | "instance_equipped"
  | "quantity_invalid";

// M5.2 — quest interaction error taxonomy. Client humanizes via
// humanizeQuestError(). `out_of_range` shares the vendor humanization
// ("Too far from the NPC.") but stays separate so the dispatch can close
// the correct modal.
export type QuestErrorCode =
  | "npc_not_found"
  | "out_of_range"
  | "quest_not_offered"
  | "already_active"
  | "already_completed"
  | "not_active"
  | "not_completable";

// Stock entry shown in the buy column, as priced by the server. Price equals
// `template.value` for M5.1 (no markup); kept on the wire for future
// per-vendor pricing without a protocol churn.
export interface VendorStockEntry {
  templateId: string;
  unitPrice: number;
}

const RequestOtp = z.object({
  t: z.literal("requestOtp"),
  d: z.object({
    email: z.string().email().max(254),
  }),
});

const VerifyOtp = z.object({
  t: z.literal("verifyOtp"),
  d: z.object({
    email: z.string().email().max(254),
    code: z.string().regex(/^\d{6}$/),
    rememberMe: z.boolean(),
  }),
});

const ResumeAuth = z.object({
  t: z.literal("auth"),
  d: z.object({
    token: z.string().min(10).max(2048),
  }),
});

const CreateCharacter = z.object({
  t: z.literal("createCharacter"),
  d: z.object({
    name: z.string().regex(/^[A-Za-z0-9]{3,16}$/),
  }),
});

const Heartbeat = z.object({
  t: z.literal("heartbeat"),
  d: z.object({}).optional(),
});

const Move = z.object({
  t: z.literal("move"),
  d: z.object({
    dx: z.union([z.literal(-1), z.literal(0), z.literal(1)]),
    dy: z.union([z.literal(-1), z.literal(0), z.literal(1)]),
    keepTarget: z.boolean().optional(),
  }),
});

const Chat = z.object({
  t: z.literal("chat"),
  d: z.object({
    channel: z.union([z.literal("global"), z.literal("local")]),
    text: z.string().min(1).max(500),
  }),
});

const WhoQuery = z.object({
  t: z.literal("who"),
  d: z.object({}).optional(),
});

const Attack = z.object({
  t: z.literal("attack"),
  d: z.object({
    mobId: z.string().min(1).max(64),
  }),
});

const CancelTarget = z.object({
  t: z.literal("cancelTarget"),
  d: z.object({}).optional(),
});

// Client toggles the per-session "send me mob debug fields" flag. Wired
// to the F3 overlay key — turning it ON starts the mobDebugSnapshot
// sidecar broadcast for this session; turning it OFF stops it.
const SetMobDebug = z.object({
  t: z.literal("setMobDebug"),
  d: z.object({ visible: z.boolean() }),
});

const AllocateStat = z.object({
  t: z.literal("allocateStat"),
  d: z.object({
    stat: z.enum(STAT_KEYS as readonly [StatKey, ...StatKey[]]),
    amount: z.number().int().min(1).max(100),
  }),
});

const EquipItem = z.object({
  t: z.literal("equipItem"),
  d: z.object({
    instanceId: z.string().min(1).max(64),
  }),
});

const UnequipItem = z.object({
  t: z.literal("unequipItem"),
  d: z.object({
    slot: z.enum(EQUIP_SLOTS as readonly [EquipSlot, ...EquipSlot[]]),
  }),
});

// M17.3 — drink a potion / eat food. Instant, zero cooldown, works while
// stunned. Identified by inventory instanceId.
const UseItem = z.object({
  t: z.literal("useItem"),
  d: z.object({
    instanceId: z.string().min(1).max(64),
  }),
});

// M17.4 — activate an ACTIVE don (Colossus Leap). The server validates
// unlock + cooldown and echoes a `donUsed` with the new tile.
const UseDon = z.object({
  t: z.literal("useDon"),
  d: z.object({
    donId: z.string().min(1).max(48),
  }),
});

const DeleteCharacter = z.object({
  t: z.literal("deleteCharacter"),
  d: z.object({}).optional(),
});

const OpenLoot = z.object({
  t: z.literal("openLoot"),
  d: z.object({
    bagId: z.string().min(1).max(64),
  }),
});

const TakeLoot = z.object({
  t: z.literal("takeLoot"),
  d: z.object({
    bagId: z.string().min(1).max(64),
    instanceId: z.string().min(1).max(64),
  }),
});

const TakeAllLoot = z.object({
  t: z.literal("takeAllLoot"),
  d: z.object({
    bagId: z.string().min(1).max(64),
  }),
});

const SetHotbarSlot = z.object({
  t: z.literal("setHotbarSlot"),
  d: z.object({
    slot: z.number().int().min(0).max(HOTBAR_SIZE - 1),
    // null clears the slot; non-null must be a known spell id (server validates
    // against the catalogue + requirement).
    spellId: z.string().min(1).max(64).nullable(),
  }),
});

const CastSpell = z.object({
  t: z.literal("castSpell"),
  d: z.object({
    slot: z.number().int().min(0).max(HOTBAR_SIZE - 1),
    // Optional explicit targets. The server validates which one matches the
    // spell's targeting kind:
    //   - damage / "single" spells (Fireball/Smite/Frost Shock) → targetMobId
    //   - heal / buff / "single" spells (Light Heal/Blessing) → targetCharacterId
    //     (which can be the caster's own id for self-cast — pattern used by
    //     Alt+hotkey or clicking your own sprite in spell-mode)
    //   - targeting "self" spells (Arcane Shield) → no target needed; server
    //     auto-targets the caster
    //   - "aoeGround" spells (Rain of Fire) → targetTile {x, y}
    targetMobId: z.string().min(1).max(64).optional(),
    targetCharacterId: z.string().min(1).max(64).optional(),
    targetTile: z
      .object({
        x: z.number().int().min(0).max(255),
        y: z.number().int().min(0).max(255),
      })
      .optional(),
  }),
});

const CancelCast = z.object({
  t: z.literal("cancelCast"),
  d: z.object({}).optional(),
});

// DEV-only: grant one level for playtesting (e.g. unlocking spells gated by
// stat thresholds without grinding). Removed before any release.
const DevGrantLevel = z.object({
  t: z.literal("devGrantLevel"),
  d: z.object({}).optional(),
});

// M5.1 — vendor NPCs. The cart UI is purely client-side; one atomic
// vendorTransact commits the whole basket on Validate. Sells are processed
// before buys server-side so "sell old sword, buy new sword" succeeds at full
// weight or low copper.
const VendorOpen = z.object({
  t: z.literal("vendorOpen"),
  d: z.object({
    npcId: z.string().min(1).max(64),
  }),
});

const VendorTransact = z.object({
  t: z.literal("vendorTransact"),
  d: z.object({
    npcId: z.string().min(1).max(64),
    buy: z
      .array(
        z.object({
          templateId: z.string().min(1).max(64),
          qty: z.number().int().min(1).max(99),
        }),
      )
      .max(32),
    sell: z
      .array(
        z.object({
          instanceId: z.string().min(1).max(64),
          qty: z.number().int().min(1).max(99),
        }),
      )
      .max(32),
  }),
});

const VendorClose = z.object({
  t: z.literal("vendorClose"),
  d: z.object({}).optional(),
});

// M5.2 — quest-giver interaction protocol. interactNpc replaces vendorOpen
// for quest-only NPCs; the client picks which to send based on the
// template's declared capabilities (vendor vs quests).
const InteractNpc = z.object({
  t: z.literal("interactNpc"),
  d: z.object({
    npcId: z.string().min(1).max(64),
  }),
});

const QuestAccept = z.object({
  t: z.literal("questAccept"),
  d: z.object({
    npcId: z.string().min(1).max(64),
    questId: z.string().min(1).max(64),
  }),
});

const QuestAbandon = z.object({
  t: z.literal("questAbandon"),
  d: z.object({
    questId: z.string().min(1).max(64),
  }),
});

const QuestTurnIn = z.object({
  t: z.literal("questTurnIn"),
  d: z.object({
    npcId: z.string().min(1).max(64),
    questId: z.string().min(1).max(64),
  }),
});

// M5.3 — party / group ops. Server is the single source of truth; every
// mutation broadcasts a partyUpdate snapshot to all affected members.
const PartyInvite = z.object({
  t: z.literal("partyInvite"),
  d: z.object({
    // Target by name, since the client doesn't know other players'
    // characterIds for offline / cross-zone invitees.
    targetCharacterName: z.string().min(1).max(32),
  }),
});

const PartyInviteAccept = z.object({
  t: z.literal("partyInviteAccept"),
  d: z.object({}).optional(),
});

const PartyInviteDecline = z.object({
  t: z.literal("partyInviteDecline"),
  d: z.object({}).optional(),
});

const PartyLeave = z.object({
  t: z.literal("partyLeave"),
  d: z.object({}).optional(),
});

const PartyKick = z.object({
  t: z.literal("partyKick"),
  d: z.object({ characterId: z.string().min(1).max(64) }),
});

const PartyPromote = z.object({
  t: z.literal("partyPromote"),
  d: z.object({ characterId: z.string().min(1).max(64) }),
});

// M7.1 — admin / authoring app messages. The admin connects over the same
// WS as players but authenticates via password (env-var ADMIN_PASSWORD)
// instead of OTP. After adminLogin the session is flagged `isAdmin` and
// can call admin* mutators. The catalogue of edit targets ("items" for
// now; "mobs", "npcs", "quests", "spells", "affixes" come in M7.2).
// Each catalogue we expose to the admin app gets a literal here + a
// corresponding adminPublish<X> message.
const CatalogueName = z.enum([
  "items",
  "mobs",
  "affixes",
  "quests",
  "npcs",
  "spells",
  "gameConfig",
  "zones",
]);

const AdminLogin = z.object({
  t: z.literal("adminLogin"),
  d: z.object({ password: z.string().min(1).max(256) }),
});

const AdminFetchCatalogue = z.object({
  t: z.literal("adminFetchCatalogue"),
  d: z.object({ catalogue: CatalogueName }),
});

// Full-replacement publish: the admin app holds the working draft locally,
// and on "Publish" sends the entire array. Server validates + writes the
// JSON file + broadcasts catalogueUpdate to every connected session.
const AdminPublishItems = z.object({
  t: z.literal("adminPublishItems"),
  d: z.object({ items: z.array(ItemTemplateSchema).min(1) }),
});

const AdminPublishMobs = z.object({
  t: z.literal("adminPublishMobs"),
  d: z.object({ mobs: z.array(MobTemplateSchema).min(1) }),
});

const AdminPublishAffixes = z.object({
  t: z.literal("adminPublishAffixes"),
  d: z.object({ affixes: AffixPoolSchema }),
});

const AdminPublishQuests = z.object({
  t: z.literal("adminPublishQuests"),
  d: z.object({ quests: z.array(QuestTemplateSchema).min(1) }),
});

const AdminPublishNpcs = z.object({
  t: z.literal("adminPublishNpcs"),
  d: z.object({ npcs: z.array(NpcTemplateSchema).min(1) }),
});

const AdminPublishSpells = z.object({
  t: z.literal("adminPublishSpells"),
  d: z.object({ spells: z.array(SpellTemplateSchema).min(1) }),
});

const AdminPublishGameConfig = z.object({
  t: z.literal("adminPublishGameConfig"),
  d: z.object({ config: GameConfigSchema }),
});

const AdminPublishZones = z.object({
  t: z.literal("adminPublishZones"),
  d: z.object({ zones: ZoneCatalogueSchema }),
});

// M11 — dev-only reboot trigger. The server flushes player positions to
// disk then process.exit(0)s; the tsx watch wrapper restarts the
// process. Useful when a zone publish needs a fresh `getWorld()` to
// re-instantiate live zones from the new catalogue.
const AdminRebootServer = z.object({
  t: z.literal("adminRebootServer"),
  d: z.object({}).optional(),
});

// M12 — OTP allowlist admin commands. listAccessRequests has an optional
// filter (status + ip exact-match). All return an accessRequestList on
// success; mutators also push the updated list back to keep the UI in sync.
const AdminListAccessRequests = z.object({
  t: z.literal("adminListAccessRequests"),
  d: z
    .object({
      status: z.enum(["pending", "approved"]).optional(),
      ip: z.string().min(1).max(64).optional(),
    })
    .optional(),
});

const AdminApproveAccessRequest = z.object({
  t: z.literal("adminApproveAccessRequest"),
  d: z.object({ email: z.string().email().max(254) }),
});

const AdminRejectAccessRequest = z.object({
  t: z.literal("adminRejectAccessRequest"),
  d: z.object({ email: z.string().email().max(254) }),
});

const AdminBulkRejectByIp = z.object({
  t: z.literal("adminBulkRejectByIp"),
  d: z.object({ ip: z.string().min(1).max(64) }),
});

const AdminAddAccessEmail = z.object({
  t: z.literal("adminAddAccessEmail"),
  d: z.object({ email: z.string().email().max(254) }),
});

// M13 — Users tab. listUsers fetches every account + character (if any) for
// the admin grid; invalidateUserSessions bumps token_version which
// (a) makes every existing JWT for that account fail on next verifySession
// (b) actively closes any live WS bound to the account.
const AdminListUsers = z.object({
  t: z.literal("adminListUsers"),
  d: z.object({}).optional(),
});

const AdminInvalidateUserSessions = z.object({
  t: z.literal("adminInvalidateUserSessions"),
  d: z.object({ accountId: z.string().min(1).max(64) }),
});

export const ClientMessageSchema = z.discriminatedUnion("t", [
  RequestOtp,
  VerifyOtp,
  ResumeAuth,
  CreateCharacter,
  Heartbeat,
  Move,
  Chat,
  WhoQuery,
  Attack,
  CancelTarget,
  SetMobDebug,
  AllocateStat,
  EquipItem,
  UnequipItem,
  UseItem,
  UseDon,
  DeleteCharacter,
  OpenLoot,
  TakeLoot,
  TakeAllLoot,
  SetHotbarSlot,
  CastSpell,
  CancelCast,
  DevGrantLevel,
  VendorOpen,
  VendorTransact,
  VendorClose,
  InteractNpc,
  QuestAccept,
  QuestAbandon,
  QuestTurnIn,
  PartyInvite,
  PartyInviteAccept,
  PartyInviteDecline,
  PartyLeave,
  PartyKick,
  PartyPromote,
  AdminLogin,
  AdminFetchCatalogue,
  AdminPublishItems,
  AdminPublishMobs,
  AdminPublishAffixes,
  AdminPublishQuests,
  AdminPublishNpcs,
  AdminPublishSpells,
  AdminPublishGameConfig,
  AdminPublishZones,
  AdminRebootServer,
  AdminListAccessRequests,
  AdminApproveAccessRequest,
  AdminRejectAccessRequest,
  AdminBulkRejectByIp,
  AdminAddAccessEmail,
  AdminListUsers,
  AdminInvalidateUserSessions,
]);

export type ClientMessage = z.infer<typeof ClientMessageSchema>;

export type ServerMessage =
  | { t: "serverHello"; d: { protocolVersion: number; serverTime: number } }
  | { t: "otpSent"; d: { email: string; devCode?: string } }
  | {
      t: "authOk";
      d: {
        token: string;
        expiresAt: number;
        accountId: string;
        hasCharacter: boolean;
        character: Character | null;
      };
    }
  | { t: "authError"; d: { reason: string } }
  | { t: "characterCreated"; d: { character: Character } }
  | { t: "characterError"; d: { reason: string } }
  | { t: "heartbeat"; d: { serverTime: number } }
  | {
      t: "presence";
      d: { onlineCount: number; players: { id: string; name: string }[] };
    }
  | { t: "rateLimited"; d: { retryAfterMs: number } }
  // M12 — OTP allowlist gate. Returned in place of `otpSent` when the
  // requesting email is unknown (not in `accounts`, not approved in
  // `email_allowlist`). The tester is told to wait for manual validation.
  | { t: "accessPending"; d: { email: string } }
  // M12 — per-IP rate limit on requestOtp triggered. Distinct from the
  // session-bound `rateLimited` so the UI can communicate "too many
  // tries from your network, retry in N min" rather than a generic flood.
  | { t: "accessRateLimited"; d: { retryAfterMs: number } }
  // M12 — admin payload for the access-requests screen. `pendingCount` lets
  // any other admin tab render a badge (e.g. "Access (3)") without a second
  // round trip. Pushed back to the admin client after every mutation.
  | {
      t: "accessRequestList";
      d: {
        rows: {
          email: string;
          status: "pending" | "approved";
          source: "self_request" | "admin_added";
          firstRequestAt: number;
          lastRequestAt: number;
          requestCount: number;
          ip: string | null;
          approvedAt: number | null;
          approvedBy: string | null;
        }[];
        pendingCount: number;
      };
    }
  | { t: "accessRequestError"; d: { reason: string } }
  // M13 — admin Users tab payloads.
  | {
      t: "userList";
      d: {
        rows: {
          id: string;
          email: string;
          createdAt: number;
          lastLoginAt: number;
          tokenVersion: number;
          characterName: string | null;
        }[];
      };
    }
  | { t: "userActionError"; d: { reason: string } }
  | {
      // M11 — metadata-only zone snapshot. Terrain + collision arrive
      // separately via `chunkData` messages, pushed by the server based
      // on the player's viewport. The client can render the static
      // overlays (spawnPoints, portals) immediately and waits on chunks
      // to display the actual tiles.
      t: "zoneEnter";
      d: {
        zoneId: string;
        width: number;
        height: number;
        isSafe: boolean;
        spawnPoints: SpawnPoint[];
        portals: Portal[];
        selfCharacterId: string;
        x: number;
        y: number;
        facing: Facing;
      };
    }
  | {
      // M11 — one chunk of terrain + collision data. The server pushes
      // these proactively after zoneEnter (initial viewport) and again
      // whenever the player crosses into a new chunk grid (incremental
      // streaming). Edge chunks may have width/height < CHUNK_SIZE when
      // the zone dimensions aren't multiples of 32.
      t: "chunkData";
      d: {
        zoneId: string;
        cx: number;
        cy: number;
        width: number;
        height: number;
        terrain: TerrainId[];
        collision: boolean[];
        // M14 — decor placements within this chunk. Empty array when the
        // zone has no decor for this chunk. Server always sets it (the
        // shared extractChunk helper guarantees it), so it's required.
        decor: { kind: string; tx: number; ty: number }[];
      };
    }
  | {
      t: "worldSnapshot";
      d: {
        tick: number;
        serverTime: number;
        players: PlayerSnapshot[];
        mobs: MobSnapshot[];
        projectiles: ProjectileSnapshot[];
      };
    }
  | {
      // Sidecar payload sent ONLY to players who toggled the F3 debug
      // overlay via `setMobDebug`. Carries per-mob internals (swing
      // timer, LOS-miss counter, current target) that the regular
      // worldSnapshot now omits. The client merges these into its
      // entity views by `id`.
      t: "mobDebugSnapshot";
      d: { zoneId: string; mobs: MobDebugSnapshot[] };
    }
  | { t: "chatPosted"; d: ChatMessage }
  | { t: "chatError"; d: { reason: string } }
  | { t: "moveRejected"; d: { x: number; y: number; reason: string } }
  | {
      t: "combatHit";
      d: {
        attackerId: string;
        attackerKind: "player" | "mob";
        targetId: string;
        targetKind: "player" | "mob";
        damage: number;
        crit: boolean;
        // "melee" = close-combat swing; "ranged" = spell projectile, AOE
        // tick, or non-magic projectile. Client uses this to gate the
        // punch animation (melee only).
        source: "melee" | "ranged";
      };
    }
  | {
      t: "combatHeal";
      d: {
        // Caster's character id (also the source-of-truth for the float
        // position used by the green heal floater).
        casterId: string;
        // Target who received the heal (may equal casterId for self-cast).
        targetCharacterId: string;
        amount: number;
        spellId: string;
      };
    }
  | {
      t: "projectileSpawned";
      d: {
        projectileId: string;
        kind: ProjectileKind;
        attackerId: string;
        attackerKind: "player" | "mob";
        originX: number;
        originY: number;
      };
    }
  | {
      t: "projectileExpired";
      d: {
        projectileId: string;
        x: number;
        y: number;
        // 'land' = hit a target (combatHit emitted alongside)
        // 'wall' = blocked by terrain
        // 'lifetime' = max travel time exceeded (defensive cap)
        // 'lost' = target died, zoned out, or was never resolvable
        reason: "land" | "wall" | "lifetime" | "lost";
      };
    }
  | {
      t: "entityDied";
      d: {
        id: string;
        kind: "player" | "mob";
        killerId: string | null;
        killerKind: "player" | "mob" | null;
      };
    }
  | {
      t: "entityRespawned";
      d: { id: string; kind: "player" | "mob" };
    }
  | {
      t: "selfStats";
      d: {
        hp: number;
        maxHp: number;
        // M8 — single mana pool (was: manaArc/manaDiv).
        mana: number;
        maxMana: number;
        inCombat: boolean;
        lifeState: "alive" | "dead";
        respawnAt: number | null;
        level: number;
        xp: number;
        xpToNext: number;
        unspentPoints: number;
        stats: CharacterStats;          // base stats (pre-affix), used for the +/allocate UI
        effectiveStats: CharacterStats; // base + affix stat bonuses
        defense: number;
        meleeDmgPct: number;
        rangedDmgPct: number;
        spellDmgPct: number;
        critPct: number;       // additive on top of dex-based crit
        // M8 — 7 resists (physical + 6 schools).
        resistPhysPct: number;
        resistDivinePct: number;
        resistNecromancyPct: number;
        resistWaterPct: number;
        resistFirePct: number;
        resistAirPct: number;
        resistEarthPct: number;
        copper: number;
        currentWeight: number;
        maxWeight: number;
        // M17.4 — ids of dons unlocked by the current BASE stats. The client
        // diffs this to fire the unlock toast and render the Dons panel.
        unlockedPassives: string[];
      };
    }
  | {
      t: "xpGained";
      d: {
        amount: number;
        source: "kill" | "death" | "quest";
        mobId?: string;
        questId?: string;
      };
    }
  | {
      t: "goldGained";
      d: {
        amount: number; // copper
        source: "kill" | "quest";
        mobId?: string;
        questId?: string;
      };
    }
  | {
      t: "levelUp";
      d: {
        newLevel: number;
        unspentPoints: number;
        maxHp: number;
        maxMana: number;
      };
    }
  | { t: "attackError"; d: { reason: string } }
  | { t: "allocateError"; d: { reason: string } }
  | {
      t: "inventoryUpdate";
      d: {
        inventory: ItemInstance[];
        equipped: EquippedSlots;
      };
    }
  | { t: "equipError"; d: { reason: string } }
  | { t: "useItemError"; d: { reason: string } }
  // M17.4 — an active don fired; the client snaps the local player to (x,y).
  | { t: "donUsed"; d: { donId: string; x: number; y: number } }
  | { t: "characterDeleted"; d: { accountId: string } }
  | {
      t: "groundBagsUpdate";
      d: {
        zoneId: string;
        bags: { bagId: string; x: number; y: number; itemCount: number; expiresAt: number }[];
      };
    }
  | {
      t: "lootContents";
      d: {
        bagId: string;
        items: ItemInstance[];
      };
    }
  | { t: "lootError"; d: { reason: string } }
  | {
      t: "hotbarUpdate";
      d: {
        hotbar: HotbarSlots;
        knownSpellIds: string[];
      };
    }
  | { t: "hotbarError"; d: { reason: string } }
  | {
      t: "castStateUpdate";
      d: {
        // null = idle; otherwise the spell being cast / auto-cycling.
        spellId: string | null;
        // Absolute serverTime when the current cast resolves. null when idle.
        castEndsAt: number | null;
        // Scaled (post-RAP) cast time so the client can size the cast bar.
        // 0 for instant spells.
        castDurationMs: number;
      };
    }
  | { t: "castError"; d: { reason: string } }
  | {
      t: "buffUpdate";
      d: {
        // Which player carries these buffs. Zone-wide broadcast so allies see
        // each other's glows + tracker icons.
        playerId: string;
        buffs: BuffSnapshot[];
      };
    }
  | {
      t: "areaEffectSpawned";
      d: {
        // Persistent ground-targeted spell effect (e.g. Rain of Fire). Zone
        // broadcast. The client renders a ring at (x, y) with the given
        // radius for the duration window [now, expiresAt].
        areaId: string;
        spellId: string;
        casterId: string;
        x: number;
        y: number;
        radius: number;
        expiresAt: number;
      };
    }
  | {
      t: "areaEffectExpired";
      d: { areaId: string; zoneId: string };
    }
  | {
      // M5.1 — one-shot snapshot of all NPCs in the player's current zone.
      // Sent on enterWorld and after each teleport. NPCs are static so they
      // don't ride the 20Hz worldSnapshot.
      t: "npcsInZone";
      d: { zoneId: string; npcs: NpcSnapshot[] };
    }
  | {
      // Server reply to vendorOpen — the buy column for this NPC. The sell
      // column is composed client-side from the player's existing inventory
      // cache (no need to ship it twice).
      t: "vendorPanel";
      d: {
        npcId: string;
        templateId: string;
        stock: VendorStockEntry[];
        sellMultiplier: number;
      };
    }
  | {
      // Server commit ack for vendorTransact. copperDelta is the signed net
      // change to the player's copper (positive = net gain from sells).
      t: "vendorResult";
      d: { npcId: string; copperDelta: number };
    }
  | { t: "vendorError"; d: { code: VendorErrorCode } }
  | {
      // Zone-broadcast: an NPC says something. Rendered as a floating bubble
      // above the NPC for `durationMs` (defaults applied client-side when
      // omitted). Currently fired after a vendorTransact so nearby players
      // also see the farewell line; future use: ambient lines, quest hooks.
      t: "npcSpeech";
      d: { npcId: string; text: string; durationMs?: number };
    }
  // ---------------------------------------------------------------------------
  // M5.2 — quest-giver interaction protocol.
  // ---------------------------------------------------------------------------
  | {
      // Server reply to interactNpc when the NPC is a quest-giver. The three
      // arrays segment the offered quests by what the player can act on; the
      // client picks the tab based on the row's status (available / active /
      // completable). Completed quests are filtered server-side (one-shot).
      t: "npcDialog";
      d: {
        npcId: string;
        templateId: string;
        available: { questId: string }[];
        active: { questId: string; progress: number[] }[];
        completable: { questId: string; progress: number[] }[];
      };
    }
  | {
      // Full quest log dump. Sent on enterWorld and any time the player's
      // log mutates (accept/abandon/kill-credit/turn-in). Always
      // authoritative; client mirrors the full array.
      t: "questLogUpdate";
      d: { questLog: import("./data/quests.js").QuestLogEntry[] };
    }
  | { t: "questError"; d: { code: QuestErrorCode } }
  // ---------------------------------------------------------------------------
  // M5.3 — party / group protocol.
  // ---------------------------------------------------------------------------
  | {
      // Sent on enterWorld + every membership / promote / online-status
      // change. `party: null` means the recipient has no party right now
      // (or just got kicked / left / their party disbanded). Always
      // authoritative — client mirrors the latest snapshot.
      t: "partyUpdate";
      d: { party: import("./data/parties.js").PartySnapshot | null };
    }
  | {
      // Inbox event for the invitee. UI surfaces an Accept / Decline popup
      // anchored on the inviter's name. Auto-clears on TTL expiry; server
      // does not push an explicit "expired" notification (silent decay).
      t: "partyInviteIncoming";
      d: {
        fromCharacterId: string;
        fromCharacterName: string;
        expiresAt: number;
      };
    }
  | { t: "partyError"; d: { code: import("./data/parties.js").PartyErrorCode } }
  // M7.1 — content authoring. `catalogueUpdate` is the unified push: sent
  // to a fresh player session after `zoneEnter` (initial snapshot) AND
  // broadcast to every session when an admin publishes a new draft.
  // Recipients call `setItemCatalogue` (or sibling) to swap the runtime
  // registry. Additional `catalogue` values land in M7.2.
  | {
      t: "catalogueUpdate";
      d:
        | { catalogue: "items"; items: ItemTemplate[] }
        | { catalogue: "mobs"; mobs: MobTemplate[] }
        | { catalogue: "affixes"; affixes: AffixDefinition[] }
        | { catalogue: "quests"; quests: QuestTemplate[] }
        | { catalogue: "npcs"; npcs: NpcTemplate[] }
        | { catalogue: "spells"; spells: SpellTemplate[] }
        | { catalogue: "gameConfig"; config: GameConfig }
        | { catalogue: "zones"; zones: ZoneJson[] };
    }
  | { t: "adminLoginOk"; d: { authenticatedAt: number } }
  | {
      t: "adminLoginError";
      d: { reason: "wrong_password" | "missing_password_env" };
    }
  | {
      t: "adminPublishResult";
      d:
        | {
            ok: true;
            catalogue:
              | "items"
              | "mobs"
              | "affixes"
              | "quests"
              | "npcs"
              | "spells"
              | "gameConfig"
              | "zones";
            count: number;
          }
        | {
            ok: false;
            catalogue:
              | "items"
              | "mobs"
              | "affixes"
              | "quests"
              | "npcs"
              | "spells"
              | "gameConfig"
              | "zones";
            error: string;
          };
    };

export type ServerMessageType = ServerMessage["t"];

export function encodeServerMessage(msg: ServerMessage): string {
  return JSON.stringify(msg);
}
